import clsx from 'clsx';
import { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  IMenuActions,
  IMenuAtag,
  IMenuButton,
  IMenuLink,
  IMenuTypes,
  isIMenuAtag,
  isIMenuButton,
  isIMenuLink,
  isMenuActions
} from 'utils/models';

// components
import Button from './Button';
import MenuAction from './MenuAction';

interface ISideActions {
  actions: IMenuTypes[];
  isCollapse?: boolean; // when true => icon-only
}

const Tooltip: FC<{ label: string }> = ({ label }) => {
  return (
    <div
      className={clsx(
        'pointer-events-none ',
        'absolute left-[40px] top-1/2 -translate-y-1/2',
        'z-50',
        'opacity-0 group-hover:opacity-100',
        'transition-opacity duration-150',
        'bg-white text-black',
        'text-xxs font-NectorMono-Regular font-light',
        'px-3 py-1 rounded-full shadow-lg',
        'whitespace-nowrap normal-case'
      )}
    >
      {label}
    </div>
  );
};

const isActiveFor = (pathname: string, paths: string[]) => {
  const clean = (s: string) => (s || '').replace(/\/+$/, '');
  const p = clean(pathname);

  return paths.some((base) => {
    const b = clean(base);
    if (!b) return false;
    return p === b || p.startsWith(b + '/');
  });
};

const SideActions: FC<ISideActions> = ({ actions, isCollapse = true }) => {
  return (
    <div className="relative">
      {actions
        .filter((item: IMenuTypes) => !item.setting)
        .map((item: IMenuTypes, index: number) => (
          <div key={index}>
            {isIMenuLink(item) && <NavLink item={item} isCollapse={isCollapse} />}
            {isIMenuAtag(item) && <HrefNav item={item} isCollapse={isCollapse} />}
            {isIMenuButton(item) && <NavButton item={item} isCollapse={isCollapse} />}
            {isMenuActions(item) && <MenuActionItems item={item} isCollapse={isCollapse} />}

            {item.divider && <div className="my-4 border border-gray-100" />}
          </div>
        ))}

      <div className="absolute bottom-0 w-full ">
        {actions
          .filter((item: IMenuTypes) => item.setting)
          .map((item: IMenuTypes, index: number) => (
            <div key={index}>
              {isIMenuLink(item) && <NavLink item={item} isCollapse={isCollapse} />}
              {isIMenuAtag(item) && <HrefNav item={item} isCollapse={isCollapse} />}
              {isIMenuButton(item) && <NavButton item={item} isCollapse={isCollapse} />}
              {isMenuActions(item) && <MenuActionItems item={item} isCollapse={isCollapse} />}

              {item.divider && <div className="my-4 border border-gray-100" />}
            </div>
          ))}
      </div>
    </div>
  );
};

// MENU (dropdown group)
interface IMenuActionItems {
  item: IMenuActions;
  isCollapse?: boolean;
}

const MenuActionItems: FC<IMenuActionItems> = ({ item, isCollapse = true }) => {
  const { pathname } = useLocation();

  const childPaths = item.actions.filter((a): a is IMenuLink => isIMenuLink(a)).map((a) => a.to);

  const extra = (item.activeMatches ?? [])
    .map((m) => (typeof m === 'string' ? m : (m as any).path))
    .filter(Boolean) as string[];

  const active = isActiveFor(pathname, [...childPaths, ...extra]);

  return (
    <MenuAction
      placement="right-start"
      classNameMain="w-full"
      menuButton={
        <div className="relative group">
          <div
            className={clsx(
              'flex items-center justify-center',
              'mb-4 px-2',
              'hover:bg-gray-100 hover:rounded-full',
              active && 'bg-yellow-100 w-full h-full rounded-full'
            )}
          >
            <Button
              variant="is-link"
              size="inline"
              rounded={item?.itemRound || 'none'}
              className="w-full"
              classNameButton={clsx('flex items-center justify-center', 'py-2')}
            >
              <div
                className={clsx(
                  'text-hazel-solid-green-500 transition-opacity',
                  active ? 'opacity-100 !text-hazel-nude-100' : 'opacity-30'
                )}
              >
                {item.icon}
              </div>
            </Button>
          </div>

          {isCollapse && <Tooltip label={item.label} />}
        </div>
      }
      menu={item.actions}
    />
  );
};

// LINK
interface INavLink {
  item: IMenuLink;
  isCollapse?: boolean;
}

const NavLink: FC<INavLink> = ({ item, isCollapse = true }) => {
  const { pathname } = useLocation();

  const matchers = [
    item.to,
    ...(item.activeMatches ?? [])
      .map((m) => (typeof m === 'string' ? m : (m as any).path))
      .filter(Boolean)
  ] as string[];

  const active = isActiveFor(pathname, matchers);

  return (
    <Link to={item.to} className="relative group block">
      <div
        className={clsx(
          'flex items-center justify-center mb-4',
          'py-2 px-2',
          'hover:bg-gray-100 hover:rounded-full',
          active && 'bg-yellow-100 w-full h-full rounded-full'
        )}
      >
        {item.icon && (
          <div
            className={clsx(
              'text-hazel-solid-green-500 transition-opacity',
              active ? '!text-hazel-nude-100 opacity-100' : 'opacity-30 group-hover:opacity-100'
            )}
          >
            {item.icon}
          </div>
        )}
      </div>

      {isCollapse && <Tooltip label={item.label} />}
    </Link>
  );
};

// ANCHOR TAG
interface IHrefNav {
  item: IMenuAtag;
  isCollapse?: boolean;
}

const HrefNav: FC<IHrefNav> = ({ item, isCollapse = true }) => {
  return (
    <a href={item.href} target={item.target} rel="noreferrer" className="relative group block">
      <div
        className={clsx(
          'flex items-center justify-center',
          'py-4',
          'hover:bg-gray-50 hover:rounded-full'
        )}
      >
        {item.icon && <div className="text-hazel-solid-green-500">{item.icon}</div>}
      </div>

      {isCollapse && <Tooltip label={item.label} />}
    </a>
  );
};

// BUTTON ITEM
interface INavButton {
  item: IMenuButton;
  isCollapse?: boolean;
}

const NavButton: FC<INavButton> = ({ item, isCollapse = true }) => {
  return (
    <div className="relative group">
      <Button
        variant="invisible"
        type={item.type}
        onClick={item.onClick}
        size="inline"
        rounded={item?.itemRound || 'none'}
        className="w-full"
        classNameButton={clsx(
          'flex items-center justify-center',
          'py-4',
          'hover:bg-gray-50 hover:rounded-full'
        )}
      >
        {item.icon && <div className="text-hazel-solid-green-500">{item.icon}</div>}
      </Button>

      {isCollapse && <Tooltip label={item.label} />}
    </div>
  );
};

export default SideActions;
